import React, { useEffect, useState } from 'react';
import FestivalGreeting from '../../components/festival/FestivalGreeting';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Building2, Package, IndianRupee, Users, AlertTriangle, FolderOpen } from 'lucide-react';
import api from '../../utils/api';
import { STATUS_STYLES, formatINR, formatDate, daysLabel, errMsg } from './saUtils';

function Stat({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="card p-4 flex items-start gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon size={20} />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-slate-900 leading-tight">{value}</p>
        {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

export default function SuperAdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/superadmin/dashboard')
      .then(res => setData(res.data))
      .catch(err => toast.error(errMsg(err, 'Could not load dashboard')));
  }, []);

  if (!data) {
    return <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" /></div>;
  }

  const { organizations: o, packages: p, revenue, primary, recent_subscriptions } = data;
  const po = primary.organization;
  const st = po ? STATUS_STYLES[po.effective_status] : null;

  return (
    <div className="space-y-6 max-w-7xl">
      <FestivalGreeting />
      <div>
        <h1 className="text-xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500">Overview of organizations, packages and subscriptions</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Stat icon={Building2} label="Organizations" value={o.total} sub={`${o.active} active · ${o.suspended} suspended · ${o.expired} expired`} color="bg-sky-50 text-sky-600" />
        <Stat icon={Package} label="Packages" value={p.total} sub={`${p.active} active`} color="bg-violet-50 text-violet-600" />
        <Stat icon={IndianRupee} label="Revenue (total)" value={formatINR(revenue.total)} sub={`This month: ${formatINR(revenue.this_month)}`} color="bg-emerald-50 text-emerald-600" />
        <Stat icon={AlertTriangle} label="Expiring ≤ 15 days" value={o.expiring_soon.length} sub="Renewal due" color="bg-amber-50 text-amber-600" />
      </div>

      {/* Live organization (this login) */}
      {po && (
        <div className="card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide">Live organization (login on this URL)</p>
              <h2 className="text-lg font-bold text-slate-900">{po.name}</h2>
              <p className="text-sm text-slate-500">
                {po.package_name || 'No package assigned'} · Expiry: {formatDate(po.expiry_date)} ({daysLabel(po.days_left)})
              </p>
            </div>
            <div className="flex items-center gap-2">
              {st && <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${st.cls}`}>{st.label}</span>}
              <Link to="/superadmin/organizations" className="btn-secondary !py-1.5 !px-3 !text-xs">Manage</Link>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              ['Users', primary.users.total, Users],
              ['Admins', primary.users.admins, Users],
              ['Workers', primary.users.workers, Users],
              ['Clients', primary.users.clients, Users],
              ['Projects', primary.projects, FolderOpen],
            ].map(([label, val, Icon]) => (
              <div key={label} className="rounded-xl bg-slate-50 p-3">
                <p className="text-xs text-slate-500 flex items-center gap-1"><Icon size={12} /> {label}</p>
                <p className="text-lg font-bold text-slate-900">{val}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="card p-5">
          <h3 className="font-bold text-slate-900 mb-3">Expiring soon</h3>
          {o.expiring_soon.length === 0 ? (
            <p className="text-sm text-slate-500">No expiries in the next 15 days.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {o.expiring_soon.map(org => (
                <li key={org.id} className="py-2.5 flex justify-between gap-3 text-sm">
                  <span className="font-medium text-slate-800 truncate">{org.name}</span>
                  <span className="text-amber-700 whitespace-nowrap">{formatDate(org.expiry_date)} · {daysLabel(org.days_left)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card p-5">
          <h3 className="font-bold text-slate-900 mb-3">Recent subscriptions</h3>
          {recent_subscriptions.length === 0 ? (
            <p className="text-sm text-slate-500">No subscription records yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {recent_subscriptions.map(s => (
                <li key={s.id} className="py-2.5 flex justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 truncate">{s.organization_name || '—'}</p>
                    <p className="text-xs text-slate-500">{s.package_name} · {formatDate(s.start_date)} → {formatDate(s.end_date)}</p>
                  </div>
                  <span className="font-semibold text-emerald-700 whitespace-nowrap">{formatINR(s.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
